import React, { useState, useContext, useRef, useMemo } from 'react';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Alert from 'react-bootstrap/Alert';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import InputGroup from 'react-bootstrap/InputGroup';
import TermsConditionsModal from '../about/TermsConditionsModal';
import PrivacyPolicyModal from '../about/PrivacyPolicyModal';
import LoginModal from './LoginModal';
import { AppContext } from '../window';
import { AuthorizedUser } from '../models/User';
import Loader from '../libs/Loader';
import { NotificationContext } from '../context/NotificationContext';
import RecaptchaV2, { RecaptchaV2Handle } from '../libs/RecaptchaV2';
import BirthDatePicker, { BirthDateChangePayload } from '../components/BirthDatePicker';
import EmailIcon from '@mui/icons-material/Email';
import PersonIcon from '@mui/icons-material/Person';
import LockIcon from '@mui/icons-material/Lock';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import WcIcon from '@mui/icons-material/Wc';
import { logDataLayerEvent } from '../utils/tagManager';
import { getEmailAndNicknameFromQuery } from '../utils/queryPrefill';
import { useTranslation, Trans } from '../i18n';

interface SignupProps {
  onSignupSuccess?: () => void;
  initialMessage?: string;
  submitButtonText?: string;
  className?: string;
  initialGender?: string;
  initialAgeRange?: string;
}

const Signup: React.FC<SignupProps> = ({ onSignupSuccess, initialMessage, submitButtonText, className, initialGender, initialAgeRange }) => {
  const { t } = useTranslation();
  const defaultSubmitButtonText = submitButtonText || t('auth.create_account');
  // Calculate default age from age range
  const getDefaultAge = (ageRange?: string) => {
    if (!ageRange) return { day: '', month: '', year: '' };
    
    const currentYear = new Date().getFullYear();
    let age = 25; // default
    
    switch(ageRange) {
      case '18-24': age = 21; break;
      case '25-34': age = 29; break;
      case '35-44': age = 39; break;
      case '45+': age = 48; break;
    }
    
    const birthYear = currentYear - age;
    return { day: '15', month: '6', year: birthYear.toString() };
  };

  const prefilledValues = useMemo(() => getEmailAndNicknameFromQuery(), []);
  const defaultAge = getDefaultAge(initialAgeRange);
  
  const [formData, setFormData] = useState({
    email: prefilledValues.email,
    password: '',
    nickname: prefilledValues.nickname,
    day: defaultAge.day,
    month: defaultAge.month,
    year: defaultAge.year,
    gender: initialGender || 'male',
    agreeToTerms: false
  });
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [recaptchaToken, setRecaptchaToken] = useState<string | null>(null);
  const appContext = useContext(AppContext);
  const { addNotification } = useContext(NotificationContext);
  const recaptchaRef = useRef<RecaptchaV2Handle>(null);
  const formDataRef = useRef(formData);
  formDataRef.current = formData;
  const [showTerms, setShowTerms] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [showLogin, setShowLogin] = useState(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleBirthDateChange = (change: BirthDateChangePayload) => {
    setFormData(prev => {
      const updated = {
        ...prev,
        day: change.parts.day,
        month: change.parts.month,
        year: change.parts.year,
      };
      formDataRef.current = updated;
      return updated;
    });
  };

  const validateForm = (data: typeof formData) => {

    if (!data.email || !data.password || !data.nickname) {
      return t('auth.validation_all_fields');
    }
    
    if (!data.email.includes('@')) {
      return t('auth.validation_valid_email');
    }
    
    if (data.password.length < 6) {
      return t('auth.validation_password_length');
    }
    
    if (!data.day || !data.month || !data.year) {
      return t('auth.validation_birth_date');
    }
    
    if (!data.gender) {
      return t('auth.validation_gender');
    }
    
    if (!data.agreeToTerms) {
      return t('auth.validation_agree_terms');
    }
    
    return null;
  };

  const handleRecaptchaVerify = async (token: string) => {
    setRecaptchaToken(token);
    // Automatically submit the form once we have the token
    if (token) {
      submitFormWithToken(formDataRef.current, token);
    }
  };

  const handleRecaptchaExpire = () => {
    setRecaptchaToken(null);
  };

  const submitFormWithToken = async (data: typeof formData, token?: string) => {
    setIsSubmitting(true);

    try {
      const submitData = new URLSearchParams();
      submitData.append('UserCredentials[email]', data.email);
      submitData.append('UserCredentials[password]', data.password);
      submitData.append('User[nickname]', data.nickname);
      submitData.append('User[day]', data.day);
      submitData.append('User[month]', data.month);
      submitData.append('User[year]', data.year);
      submitData.append('User[gender]', data.gender);
      submitData.append('User[tcAgree]', data.agreeToTerms ? '1' : '0');
      
      // Add reCAPTCHA token if available
      if (token || recaptchaToken) {
        submitData.append('g-recaptcha-response', token || recaptchaToken || '');
      }

      const response = await fetch('/user/signup/do?json=1', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: submitData.toString(),
      });
      
      const responseData = await response.json();
      
      if (responseData.success) {
        setFormData({
          email: '',
          password: '',
          nickname: '',
          day: '',
          month: '',
          year: '',
          gender: 'male',
          agreeToTerms: false
        });
        
        const responseUser = responseData.user as AuthorizedUser | undefined;
        if (responseUser) {
          appContext.setUser(responseUser);
        }
        addNotification({
          level: 'success',
          message: t('auth.signup_success'),
        });
        logDataLayerEvent('signup', {}, responseUser ?? appContext.user);
        if (onSignupSuccess) {
          onSignupSuccess();
        }
      } else {
        setErrorMessage(responseData.error || t('auth.registration_failed'));
      }
    } catch (error) {
      setErrorMessage(t('auth.error_occurred'));
      console.error('Signup error:', error);
    } finally {
      setIsSubmitting(false);
      // Reset reCAPTCHA after submission
      if (recaptchaRef.current && recaptchaRef.current.reset) {
        recaptchaRef.current.reset();
      }
      setRecaptchaToken(null);
    }
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setErrorMessage(null);
    
    const validationError = validateForm(formData);
    if (validationError) {
      setErrorMessage(validationError);
      return;
    }

    // If we have reCAPTCHA but no token, execute it
    if (appContext.recaptchaSiteKey && !recaptchaToken) {
      if (recaptchaRef.current && recaptchaRef.current.execute) {
        recaptchaRef.current.execute();
      }
      return;
    }
    
    // If no reCAPTCHA required or we already have a token, submit directly
    submitFormWithToken(formData, recaptchaToken || undefined);
  };

  return (
    <div className={`signup-form ${className || ''}`}>
      {initialMessage && (
        <Alert variant="info" className="mb-3">
          <small>{t('auth.signup_message_alert', { initialMessage })}</small>
        </Alert>
      )}

      {errorMessage && (
        <Alert variant="danger">{errorMessage}</Alert>
      )}

      {isSubmitting && (
        <Loader />
      )}

      <Form onSubmit={handleSubmit} className={isSubmitting ? 'd-none' : ''}>
        <Row>
          <Col md={6}>
            <InputGroup className="mb-3">
              <InputGroup.Text>
                <EmailIcon />
              </InputGroup.Text>
              <Form.Floating>
                <Form.Control 
                  type="email" 
                  name="email"
                  placeholder={t('auth.enter_email')}
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                />
                <Form.Label>{t('auth.email_required')}</Form.Label>
              </Form.Floating>
            </InputGroup>
          </Col>
          <Col md={6}>
            <InputGroup className="mb-3">
              <InputGroup.Text>
                <LockIcon />
              </InputGroup.Text>
              <Form.Floating>
                <Form.Control 
                  type="password" 
                  name="password"
                  placeholder={t('auth.password')}
                  value={formData.password}
                  onChange={handleInputChange}
                  required
                />
                <Form.Label>{t('auth.password_required')}</Form.Label>
              </Form.Floating>
            </InputGroup>
          </Col>
        </Row>

        <InputGroup className="mb-3">
          <InputGroup.Text>
            <PersonIcon />
          </InputGroup.Text>
          <Form.Floating>
            <Form.Control 
              type="text" 
              name="nickname"
              placeholder={t('auth.enter_nickname')}
              value={formData.nickname}
              onChange={handleInputChange}
              required
            />
            <Form.Label>{t('auth.nickname_required')}</Form.Label>
          </Form.Floating>
        </InputGroup>

        <BirthDatePicker
          value={{
            day: formData.day,
            month: formData.month,
            year: formData.year,
          }}
          onChange={handleBirthDateChange}
          className="mb-3 flex-grow-1"
          variant="input-group"
          prefix={<CalendarMonthIcon />}
        />

        <InputGroup className="mb-3">
          <InputGroup.Text>
            <WcIcon />
          </InputGroup.Text>
          <Form.Floating>
            <Form.Select name="gender" value={formData.gender} onChange={handleInputChange} required>
              <option value="">{t('auth.select_gender')}</option>
              {appContext.data.attributes.filter(attr => attr.name === 'gender').map((attr) => (
                <option key={attr.id} value={attr.value.toLowerCase()}>{attr.translatedValue}</option>
              ))}
            </Form.Select>
            <Form.Label>{t('auth.gender_required')}</Form.Label>
          </Form.Floating>
        </InputGroup>

        <Form.Check 
          type="checkbox"
          id="agreeToTerms"
          name="agreeToTerms"
          checked={formData.agreeToTerms}
          onChange={handleInputChange}
          label={
            <span style={{ fontSize: '0.875rem' }}>
              <Trans
                i18nKey="auth.tos_agree"
                components={{
                  termsLink: (
                    <a
                      href="#"
                      style={{ fontSize: '0.875rem' }}
                      onClick={(e) => {
                        e.preventDefault();
                        setShowTerms(true);
                      }}
                    />
                  ),
                  privacyLink: (
                    <a
                      href="#"
                      style={{ fontSize: '0.875rem' }}
                      onClick={(e) => {
                        e.preventDefault();
                        setShowPrivacy(true);
                      }}
                    />
                  ),
                }}
              />
            </span>
          }
          className="mb-3 form-check-lg"
          required
        />

        {appContext.recaptchaSiteKey && (
          <RecaptchaV2
            ref={recaptchaRef}
            siteKey={appContext.recaptchaSiteKey}
            onVerify={handleRecaptchaVerify}
            onExpire={handleRecaptchaExpire}
            invisible={true}
          />
        )}

        <Button 
          variant="success" 
          type="submit" 
          className="w-100"
          disabled={isSubmitting}
        >
          {isSubmitting ? t('auth.creating_account') : defaultSubmitButtonText}
        </Button>
      </Form>

      <div className="text-center mt-3">
        {t('auth.already_have_account')}{' '}
        <a
          href="#"
          onClick={(e) => {
            e.preventDefault();
            setShowLogin(true);
          }}
        >
          {t('auth.login_here')}
        </a>
      </div>
      
      <TermsConditionsModal
        show={showTerms}
        onHide={() => setShowTerms(false)}
      />
      
      <PrivacyPolicyModal
        show={showPrivacy}
        onHide={() => setShowPrivacy(false)}
      />
      
      <LoginModal
        show={showLogin}
        onHide={() => setShowLogin(false)}
        onLoginSuccess={onSignupSuccess}
      />
    </div>
  );
};

export default Signup;